import React, { useMemo } from "react";

import { Box, Typography } from "@mui/material";
import type { DroppingItem } from "components/Report-generator/context/ReportGenerator-context";
import { computeTableGridH, DRAG_HANDLE_HEIGHT, gridItemWidth } from "components/Report-generator/utils/gridUtils";
import type { LayoutItem } from "components/Report-generator/utils/gridUtils";
import { getPageMetrics } from "components/Report-generator/utils/pageMetrics";
import { splitPagesForOverflow } from "components/Report-generator/utils/splitTablePages";
import type {
  CanvasCard,
  CardType,
  PageLayout,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SizePreset
} from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

import { PAGE_LAYOUT_OPTIONS, PageView } from "./PageView";

const EMPTY_SET: Set<string> = new Set();

interface Props {
  cards: CanvasCard[];
  pages: ReportPage[];
  selectedCardId: string;
  reportMeta: ReportMeta;
  orientation: ReportOrientation;
  isResizing: boolean;
  droppingItem: DroppingItem | null;
  onResizingChange: (resizing: boolean) => void;
  onUpdateMeta: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
  onSelectCard: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDuplicateCard: (id: string) => void;
  onUpdateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  onPresetChange: (id: string, preset: SizePreset) => void;
  expandedRows: Record<string, Set<string>>;
  onToggleExpand: (cardId: string, rowKey: string) => void;
  onAddPage: (layout?: PageLayout) => void;
  onRemovePage: (page: number) => void;
  onUpdatePage: (pageIndex: number, updates: Partial<ReportPage>) => void;
  onLayoutChange: (pageIndex: number, layout: LayoutItem[]) => void;
  onDropCard: (type: CardType, pageIndex: number, gridX: number, gridY: number, resolvedLayout?: LayoutItem[]) => void;
  onOpenSettings: (id: string) => void;
  zoom: number;
}

export const Canvas: React.FC<Props> = ({
  cards,
  pages,
  selectedCardId,
  reportMeta,
  orientation,
  isResizing,
  droppingItem,
  onResizingChange,
  onUpdateMeta,
  onLogoFileChange,
  onSelectCard,
  onRemoveCard,
  onDuplicateCard,
  onUpdateCard,
  onPresetChange,
  expandedRows,
  onToggleExpand,
  onAddPage,
  onRemovePage,
  onUpdatePage,
  onLayoutChange,
  onDropCard,
  onOpenSettings,
  zoom
}) => {
  const [showAddMenu, setShowAddMenu] = React.useState(false);
  const { width: pageWidth, height: pageHeight } = getPageMetrics(orientation);

  const { resolvedPages, resolvedMeta, resolvedPageCount, cardOffsets, originalCardIds } = useMemo(() => {
    // Pre-process: compute content-driven gridH for table cards
    const adjustedCards = cards.map((card) => {
      if (card.config.type !== "table" || !card.data) return card;
      const expanded = expandedRows[card.id] ?? EMPTY_SET;
      const contentGridH = computeTableGridH(
        card,
        expanded,
        gridItemWidth(pageWidth, card.gridW),
        undefined,
        undefined,
        DRAG_HANDLE_HEIGHT,
        true
      );
      if (contentGridH === card.gridH) return card;
      return { ...card, gridH: contentGridH };
    });

    const rawGroupedPages: CanvasCard[][] = Array.from({ length: pages.length }, (_, i) =>
      adjustedCards.filter((c) => c.page === i)
    );
    const result = splitPagesForOverflow(rawGroupedPages, pages, pageHeight, pageWidth, expandedRows, {
      contentPadding: 56,
      isEditMode: true
    });

    // Post-process: recompute gridH for each table segment based on its actual
    // (sliced) data. Continuation cards inherit the original card's full-content
    // gridH via spread, so they must be resized to fit only their own rows.
    const sizedPages = result.pages.map((pageCards) =>
      pageCards.map((card) => {
        if (card.config.type !== "table" || !card.data) return card;
        const isContinuation = !!result.originalCardIds[card.id];
        const origId = result.originalCardIds[card.id] ?? card.id;
        const expanded = expandedRows[origId] ?? EMPTY_SET;
        // Use minGridH=1 so continuation segments with few rows aren't forced to the default minimum.
        // Pass index offsets so row keys match the ones stored in expandedRows (e.g. "root-5" not "root-0").
        // Non-continuation cards have a 28px drag handle in edit mode that reduces content area.
        const offsets = result.cardOffsets[card.id];
        const overhead = isContinuation ? 0 : DRAG_HANDLE_HEIGHT;
        const segmentGridH = computeTableGridH(
          card,
          expanded,
          gridItemWidth(pageWidth, card.gridW),
          1,
          offsets,
          overhead,
          true
        );
        if (segmentGridH === card.gridH) return card;
        return { ...card, gridH: segmentGridH };
      })
    );

    // Extend splitting tables' gridH on primary pages to reserve remaining space.
    // Prevents RGL from allowing drops below a table that continues on the next page.
    const splittingCardIds = new Set(Object.values(result.originalCardIds));
    const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;

    for (let resolvedIdx = 0; resolvedIdx < sizedPages.length; resolvedIdx++) {
      const meta = result.pageMeta[resolvedIdx];
      if (meta.isContinuation || meta.isSpillover) continue;

      const pgCards = sizedPages[resolvedIdx];
      const splittingTableIdx = pgCards.findIndex((c) => c.config.type === "table" && splittingCardIds.has(c.id));
      if (splittingTableIdx === -1) continue;

      const headerH = meta.layout === "header-content" ? 180 : meta.layout === "section" ? 150 : 0;
      const contentPadV = 32;
      const availH = pageHeight - headerH - contentPadV;
      const maxRows = Math.floor((availH + GRID_CONSTANTS.GAP) / cellPlusGap);

      const tableCard = pgCards[splittingTableIdx];
      const extendedGridH = maxRows - tableCard.gridY;

      if (extendedGridH > tableCard.gridH) {
        sizedPages[resolvedIdx] = pgCards.map((c, i) => (i === splittingTableIdx ? { ...c, gridH: extendedGridH } : c));
      }
    }

    return {
      resolvedPages: sizedPages,
      resolvedMeta: result.pageMeta,
      resolvedPageCount: sizedPages.length,
      cardOffsets: result.cardOffsets,
      originalCardIds: result.originalCardIds
    };
  }, [cards, pages, pageHeight, pageWidth, expandedRows]);

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "auto",
        minHeight: 0,
        bgcolor: "#cfd8e8",
        p: "40px 32px 80px"
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "34px",
          zoom
        }}
      >
        {resolvedPages.map((pageCards, resolvedIndex) => {
          const meta = resolvedMeta[resolvedIndex];

          if (meta.isContinuation) {
            return (
              <PageView
                key={`cont-${resolvedIndex}`}
                pageIndex={resolvedIndex}
                pageCount={resolvedPageCount}
                originalPageIndex={meta.originalPageIndex}
                page={meta}
                pageCards={pageCards}
                allCards={cards}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                selectedCardId={selectedCardId}
                reportMeta={reportMeta}
                orientation={orientation}
                isResizing={isResizing}
                droppingItem={droppingItem}
                isContinuation
                expandedRows={expandedRows}
                onToggleExpand={onToggleExpand}
                cardOffsets={cardOffsets}
                originalCardIds={originalCardIds}
                onResizingChange={onResizingChange}
                onUpdateMeta={onUpdateMeta}
                onLogoFileChange={onLogoFileChange}
                onSelectCard={onSelectCard}
                onRemoveCard={onRemoveCard}
                onDuplicateCard={onDuplicateCard}
                onUpdateCard={onUpdateCard}
                onPresetChange={onPresetChange}
                onRemovePage={() => {}}
                onUpdatePage={() => {}}
                onLayoutChange={onLayoutChange}
                onDropCard={onDropCard}
                onOpenSettings={onOpenSettings}
              />
            );
          }

          if (meta.isSpillover) {
            return (
              <PageView
                key={`spillover-${resolvedIndex}`}
                pageIndex={resolvedIndex}
                pageCount={resolvedPageCount}
                originalPageIndex={meta.originalPageIndex}
                page={meta}
                pageCards={pageCards}
                allCards={cards}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                selectedCardId={selectedCardId}
                reportMeta={reportMeta}
                orientation={orientation}
                isResizing={isResizing}
                droppingItem={droppingItem}
                expandedRows={expandedRows}
                onToggleExpand={onToggleExpand}
                cardOffsets={cardOffsets}
                originalCardIds={originalCardIds}
                onResizingChange={onResizingChange}
                onUpdateMeta={onUpdateMeta}
                onLogoFileChange={onLogoFileChange}
                onSelectCard={onSelectCard}
                onRemoveCard={onRemoveCard}
                onDuplicateCard={onDuplicateCard}
                onUpdateCard={onUpdateCard}
                onPresetChange={onPresetChange}
                onRemovePage={() => {}}
                onUpdatePage={() => {}}
                onLayoutChange={onLayoutChange}
                onDropCard={onDropCard}
                onOpenSettings={onOpenSettings}
              />
            );
          }

          return (
            <PageView
              key={`page-${meta.originalPageIndex}`}
              pageIndex={resolvedIndex}
              pageCount={resolvedPageCount}
              originalPageIndex={meta.originalPageIndex}
              page={pages[meta.originalPageIndex]}
              pageCards={pageCards}
              allCards={cards}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
              selectedCardId={selectedCardId}
              reportMeta={reportMeta}
              orientation={orientation}
              isResizing={isResizing}
              droppingItem={droppingItem}
              expandedRows={expandedRows}
              onToggleExpand={onToggleExpand}
              cardOffsets={cardOffsets}
              originalCardIds={originalCardIds}
              onResizingChange={onResizingChange}
              onUpdateMeta={onUpdateMeta}
              onLogoFileChange={onLogoFileChange}
              onSelectCard={onSelectCard}
              onRemoveCard={onRemoveCard}
              onDuplicateCard={onDuplicateCard}
              onUpdateCard={onUpdateCard}
              onPresetChange={onPresetChange}
              onRemovePage={() => onRemovePage(meta.originalPageIndex)}
              onUpdatePage={(updates) => onUpdatePage(meta.originalPageIndex, updates)}
              onLayoutChange={onLayoutChange}
              onDropCard={onDropCard}
              onOpenSettings={onOpenSettings}
            />
          );
        })}

        <Box sx={{ position: "relative", width: pageWidth, pb: 10 }}>
          <Box
            component="button"
            onClick={() => setShowAddMenu((v) => !v)}
            sx={{
              width: "100%",
              p: "18px 0",
              border: "2px dashed rgba(7,31,53,0.25)",
              borderRadius: "10px",
              bgcolor: "transparent",
              color: "rgba(7,31,53,0.55)",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Add page
          </Box>
          {showAddMenu && (
            <Box
              sx={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                mb: 1,
                bgcolor: "#FFFFFF",
                borderRadius: "10px",
                boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                border: "1px solid #dbe8f5",
                p: 1,
                display: "flex",
                gap: "6px",
                zIndex: 20,
                flexWrap: "wrap",
                justifyContent: "center"
              }}
            >
              {PAGE_LAYOUT_OPTIONS.map((option) => (
                <Box
                  key={option.value}
                  component="button"
                  onClick={() => {
                    onAddPage(option.value);
                    setShowAddMenu(false);
                  }}
                  sx={{
                    p: "10px 14px",
                    border: "1px solid #dbe8f5",
                    borderRadius: "8px",
                    bgcolor: "#FAFCFE",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    minWidth: 92
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#071f35" }}>{option.tag}</Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#071f35" }}>{option.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
